import React, { useCallback, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import {
  fetchPatronMatches,
  looksLikeMembershipNumber,
  PATRON_SEARCH_DEBOUNCE_MS,
  PATRON_SEARCH_MIN_CHARS,
  patronMembershipNumber,
  patronSearchLabel,
} from '../../../utils/patronProfileSearch'

function typeLabel(row) {
  const t = String(row?.profile_type || row?.ProfileType || 'MEMBER').toUpperCase()
  return t === 'VISITOR' ? 'Visitor' : 'Member'
}

function personLine(p) {
  if (!p) return '—'
  const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
  const bits = [
    name || p.master_profile_id,
    p.master_profile_id,
    p.membership_number ? `Member ${p.membership_number}` : null,
    p.member_id ? `Player ${p.member_id}` : null,
    `${p.payout_count || 0} payouts`,
  ].filter(Boolean)
  return bits.join(' · ')
}

export default function FoldIntoModal({
  open,
  onClose,
  currentMasterProfileId,
  currentName,
  onFolded,
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [selected, setSelected] = useState(null)
  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState('')
  const [keepRequested, setKeepRequested] = useState(false)
  const [confirmOverride, setConfirmOverride] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const debounceRef = useRef(null)
  const searchSeqRef = useRef(0)

  useEffect(() => {
    if (!open) return
    setQuery('')
    setResults([])
    setSelected(null)
    setPreview(null)
    setPreviewError('')
    setKeepRequested(false)
    setConfirmOverride(false)
    setNote('')
    setError('')
    setSearchError('')
  }, [open])

  const runSearch = useCallback(async (raw) => {
    const q = String(raw || '').trim()
    if (q.length < PATRON_SEARCH_MIN_CHARS) {
      setResults([])
      setSearchError('')
      setSearching(false)
      return
    }
    const seq = ++searchSeqRef.current
    setSearching(true)
    setSearchError('')
    try {
      const rows = await fetchPatronMatches(q)
      if (seq !== searchSeqRef.current) return
      const filtered = rows.filter(
        (r) => String(r.MasterProfileID || '') !== String(currentMasterProfileId || '')
      )
      setResults(filtered)
      if (!filtered.length) {
        setSearchError(
          looksLikeMembershipNumber(q)
            ? 'No other profile with that membership number'
            : 'No matching profiles found'
        )
      }
    } catch (e) {
      if (seq !== searchSeqRef.current) return
      setResults([])
      setSearchError(e.message || 'Search failed')
    } finally {
      if (seq === searchSeqRef.current) setSearching(false)
    }
  }, [currentMasterProfileId])

  const onQueryChange = (value) => {
    setQuery(value)
    setSelected(null)
    setPreview(null)
    setPreviewError('')
    setKeepRequested(false)
    setConfirmOverride(false)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => runSearch(value), PATRON_SEARCH_DEBOUNCE_MS)
  }

  const loadPreview = async (row) => {
    const into = String(row?.MasterProfileID || '').trim()
    if (!into || !currentMasterProfileId) return
    setSelected(row)
    setPreview(null)
    setPreviewError('')
    setError('')
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/profiles/${encodeURIComponent(currentMasterProfileId)}/fold-preview?into=${encodeURIComponent(into)}`
        )
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not recommend a keeper')
      setPreview(json.data)
      setKeepRequested(false)
      setConfirmOverride(false)
    } catch (e) {
      setPreviewError(e.message || 'Preview failed')
    }
  }

  const submit = async () => {
    const into = String(selected?.MasterProfileID || '').trim()
    if (!into) {
      setError('Select the other profile first')
      return
    }
    if (preview?.requires_override && !confirmOverride && !keepRequested) {
      setError('Tick the confirmation — these rows look like different members.')
      return
    }
    if (keepRequested && preview?.requires_override && !confirmOverride) {
      setError('Tick the confirmation to keep your direction when the keys differ.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/profiles/${encodeURIComponent(currentMasterProfileId)}/fold`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            into_master_profile_id: into,
            note: note.trim() || undefined,
            confirm_override: confirmOverride,
            keep_requested_direction: keepRequested,
          }),
        }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || 'Fold failed')
      onFolded?.(json.data)
      onClose?.()
    } catch (e) {
      setError(e.message || 'Fold failed')
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  const source = keepRequested ? preview?.requested?.source : preview?.source
  const target = keepRequested ? preview?.requested?.target : preview?.target
  const canOverrideDirection = preview?.kind === 'member_into_member' && preview?.swapped

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900 m-0">Fold into another profile</h3>
          <button type="button" className="text-slate-400 hover:text-slate-600" onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-4 py-3 space-y-3">
          <p className="text-xs text-slate-500 m-0">
            Standing on <span className="font-medium text-slate-700">{currentName || currentMasterProfileId}</span>.
            Search for the other record. We pick the keeper: visitor always folds into member; two members keep
            the club / DimPatron row (membership + player id, then payouts).
          </p>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">Find the other profile</span>
            <input
              type="search"
              className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
              placeholder="Name or membership number"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              autoComplete="off"
            />
          </label>
          {searching ? <p className="text-xs text-slate-500 m-0">Searching…</p> : null}
          {searchError ? <p className="text-xs text-amber-700 m-0">{searchError}</p> : null}
          {results.length > 0 ? (
            <ul className="m-0 p-0 list-none border border-slate-200 rounded-md divide-y divide-slate-100 max-h-40 overflow-y-auto">
              {results.map((row) => {
                const id = row.MasterProfileID
                const active = selected?.MasterProfileID === id
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => loadPreview(row)}
                      className={`w-full text-left px-3 py-2 text-sm ${active ? 'bg-teal-50' : 'hover:bg-slate-50'}`}
                    >
                      <div className="font-medium text-slate-900">{patronSearchLabel(row)}</div>
                      <div className="text-xs text-slate-500">
                        {id}
                        {patronMembershipNumber(row) ? ` · Member ${patronMembershipNumber(row)}` : ''}
                        {row.MID ? ` · Player ${row.MID}` : ''}
                        {` · ${typeLabel(row)}`}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          ) : null}

          {previewError ? <p className="text-xs text-red-600 m-0">{previewError}</p> : null}

          {preview?.ok ? (
            <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold text-slate-800 m-0">Who stays</p>
              <p className="text-xs text-slate-700 m-0">
                <span className="font-medium">Keeper (stays):</span> {personLine(target)}
              </p>
              <p className="text-xs text-slate-700 m-0">
                <span className="font-medium">Folds away (hidden from search):</span> {personLine(source)}
              </p>
              {(preview.reasons || []).map((r) => (
                <p key={r} className="text-xs text-slate-500 m-0">{r}</p>
              ))}
              {(preview.warnings || []).map((w) => (
                <p key={w} className="text-xs text-amber-800 m-0">{w}</p>
              ))}
              {canOverrideDirection ? (
                <label className="flex items-start gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={keepRequested}
                    onChange={(e) => setKeepRequested(e.target.checked)}
                  />
                  <span>Keep my selection as the keeper (fold this profile into the one I picked).</span>
                </label>
              ) : null}
              {preview.requires_override ? (
                <label className="flex items-start gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={confirmOverride}
                    onChange={(e) => setConfirmOverride(e.target.checked)}
                  />
                  <span>I confirm these are the same person even though membership or player ids differ.</span>
                </label>
              ) : null}
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Note (optional)</span>
                <input
                  type="text"
                  className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                />
              </label>
            </div>
          ) : null}

          {error ? <p className="text-xs text-red-600 m-0">{error}</p> : null}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy || !preview?.ok}>
            {busy ? 'Folding…' : 'Fold profiles'}
          </Button>
        </div>
      </div>
    </div>
  )
}
